import { Children, cloneElement, Fragment, isValidElement, type ReactNode } from "react";
import { phraseSegments } from "./phrase-break";
import { keepBangs, NUMBER_UNIT } from "./nowrap-units";
import { hasBreakMarks, markBreaks } from "./break-marks";

/**
 * 数と単位 (「第 3 章から」「10 問」「1 年」「LPIC-1」)。.lg-phrase は keep-all なので、文節の中の空白 (「第 3」と「章」の間) だけが折れ目になり、
 * 「第 3 / 章から」「10 / 問」と割れていた。数と単位の塊 (NUMBER_UNIT) は折らない塊 (.lg-nowrap) にする。
 * 英字とハイフンと数は Chrome がハイフンの後ろで折るので、同じ塊にする
 */
function keepSegment(seg: string): ReactNode {
  // 呼ぶ側が入れた折り方の印 (U+200B、U+2060) は字に残さず、wbr と折らない塊に替える (break-marks.tsx)
  if (hasBreakMarks(seg)) return markBreaks(seg, keepPlainSegment);
  return keepPlainSegment(seg);
}

function keepPlainSegment(seg: string): ReactNode {
  const parts = seg.split(NUMBER_UNIT);
  if (parts.length === 1) return keepBangs(seg);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="lg-nowrap">
        {part}
      </span>
    ) : (
      <Fragment key={i}>{keepBangs(part)}</Fragment>
    ),
  );
}

/** 末尾の文節がこの字数 (空白を除く) 以下なら短い尾。折れたとき 1〜2 字だけの行になる */
const SHORT_TAIL_LENGTH = 2;
/** 短い尾に前の語を寄せて、最後の行がこの字数 (空白を除く) 以上になるようにする */
const MIN_LAST_LINE = 5;

/**
 * 短い尾 (「解く」) が、空白で区切られた手前の語の後ろにぶら下がって 1〜2 字だけの行にならないよう、
 * 手前の語を尾に寄せて折らない塊にする (「1〜5 を解く」)。手前の文節の中に空白が無ければ (文節の境目の wbr だけなら) 何もしない。
 * 戻りは [頭の文節、塊の字]。塊が作れないときは null
 */
function bindShortTail(segs: string[]): { head: string[]; bound: string } | null {
  if (segs.length < 2) return null;
  const tail = segs[segs.length - 1];
  if (tail.replace(/\s/g, "").length > SHORT_TAIL_LENGTH) return null;
  const prev = segs[segs.length - 2];
  if (!/\s/.test(prev.trim())) return null;
  const tokens = prev.split(/(\s+)/);
  let bound = tail;
  while (tokens.length > 1 && bound.replace(/\s/g, "").length < MIN_LAST_LINE) {
    bound = (tokens.pop() ?? "") + bound;
    if (bound.replace(/\s/g, "").length < MIN_LAST_LINE && tokens.length > 0 && /^\s+$/.test(tokens[tokens.length - 1])) bound = (tokens.pop() ?? "") + bound;
  }
  const rest = tokens.join("");
  if (rest.trim() === "" && segs.length === 2) return null;
  return { head: [...segs.slice(0, -2), rest], bound: bound.replace(/^\s+/, " ") };
}

/** 字の塊 1 つを文節に分け、境目に wbr を入れて .lg-phrase に包む */
function PhraseRun({ text }: { text: string }) {
  const segs = phraseSegments(text);
  const tail = bindShortTail(segs);
  const head = tail ? tail.head.filter((seg) => seg !== "") : segs;
  return (
    <span className="lg-phrase">
      {head.map((seg, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          {keepSegment(seg)}
        </Fragment>
      ))}
      {tail ? <span className="lg-nowrap">{keepSegment(tail.bound)}</span> : null}
    </span>
  );
}

/** 中の字を触らない要素。折れ目、数と単位や長い語の塊 (折らない)、すでに文節に分けた物、コード、絵 */
const SKIP_TAGS = new Set(["wbr", "br", "svg", "img", "code", "kbd", "input", "textarea", "select", "script", "style"]);
const SKIP_CLASSES = ["lg-nowrap", "lg-phrase", "lgv-kana"];

/** 要素の前後に折れ目を足さない相手。後ろの字の塊が助詞や句読点で始まるなら、前の要素の直後では折らない */
const NO_BREAK_BEFORE = /^[のをにがとでへはもや、。，．,.)）\]］」』】〕〉》ーぁぃぅぇぉっゃゅょゎ!！?？:：;；・\s]/;

type Item = { kind: "text"; text: string } | { kind: "el"; el: ReactNode; wbr: boolean };

/**
 * 題の子を、字の塊ごとに文節で分ける。字と数の連なりは .lg-phrase に入れ、Fragment と普通の要素 (span、strong、mark) の中も辿る。
 * 折らない塊 (.lg-nowrap)、wbr、コンポーネント、絵はそのまま残す。
 * 要素と字の境目は keep-all で折れ目が無くなるので、要素の前後に wbr を足す (前の塊の最後の文節が 1 字なら足さない、後ろが助詞や句読点で始まるなら足さない)
 */
export function phraseChildren(children: ReactNode): ReactNode {
  const items: Item[] = [];
  const walk = (list: ReactNode) => {
    Children.forEach(list, (child) => {
      if (child === null || child === undefined || typeof child === "boolean") return;
      if (typeof child === "string" || typeof child === "number") {
        const text = String(child);
        if (text === "") return;
        const last = items[items.length - 1];
        if (last?.kind === "text") last.text += text;
        else items.push({ kind: "text", text });
        return;
      }
      if (isValidElement<{ children?: ReactNode; className?: string; dangerouslySetInnerHTML?: unknown }>(child)) {
        if (child.type === Fragment) {
          walk(child.props.children);
          return;
        }
        const tag = typeof child.type === "string" ? child.type : null;
        const cls = typeof child.props.className === "string" ? child.props.className.split(/\s+/) : [];
        const skipped = !tag || SKIP_TAGS.has(tag) || Boolean(child.props.dangerouslySetInnerHTML) || cls.some((c) => SKIP_CLASSES.includes(c));
        if (!skipped && child.props.children !== undefined) {
          items.push({ kind: "el", el: cloneElement(child, undefined, phraseChildren(child.props.children)), wbr: true });
          return;
        }
        items.push({ kind: "el", el: child, wbr: tag !== "wbr" && tag !== "br" });
        return;
      }
      items.push({ kind: "el", el: child, wbr: false });
    });
  };
  walk(children);
  return items.map((item, i) => {
    if (item.kind === "text") return <PhraseRun key={i} text={item.text} />;
    const prev = items[i - 1];
    const next = items[i + 1];
    const before = item.wbr && prev?.kind === "text" && !/\s$/.test(prev.text) && (phraseSegments(prev.text).pop()?.length ?? 0) >= 2;
    const after = item.wbr && next?.kind === "text" && !NO_BREAK_BEFORE.test(next.text);
    return (
      <Fragment key={i}>
        {before ? <wbr /> : null}
        {item.el}
        {after ? <wbr /> : null}
      </Fragment>
    );
  });
}

/**
 * 字だけの題や 1 文を、文節の境目にだけ折れる所 (wbr) を入れて描く。要素が混ざる子は、字の塊ごとに同じ処理を当てる (phraseChildren)。
 * Chrome は .lg の word-break: auto-phrase で文節に折るが、Safari と Firefox には無いので、
 * ここで入れた wbr と .lg-phrase の keep-all で「まずは完成形 / を確認しよう」のような助詞からの行を作らない
 */
export function LgPhrase({ children }: { children: ReactNode }) {
  return <>{phraseChildren(children)}</>;
}
